import { ChangeDetectorRef, Component, Input, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';

import { I18nService } from '@recruitee/i18n';
import { RequisitionsActions } from '@recruitee/requisitions-data';
import type { RequisitionAvailableFlow } from '@recruitee/requisitions-types';
import { HeadingComponent } from '@recruitee/user-interface';

import { RequisitionsFlowSelectComponent } from '../../../components/requisitions-flow-select/requisitions-flow-select.component';
import { ApprovalFlowFacade } from '../../../facades/single-requisition/approval-flow.facade';
import { FlowSelectOption } from '../../../requisitions.types';

@Component({
  selector: 'rt-requisitions-flow-select-container',
  template: `
    @if (isSelectFlowVisible) {
      <rt-requisitions-flow-select
        [isDisabled]="isSelectFlowDisabled"
        [options]="options"
        [selectedOption]="selectedOption"
        (select)="setFlow($event)"
      >
      </rt-requisitions-flow-select>
    }
  `,
  imports: [RequisitionsFlowSelectComponent, HeadingComponent],
  standalone: true,
})
export class RequisitionsFlowSelectContainerComponent {
  private readonly customFlowLabel = this.i18n.translate('app.requisitions.flow.custom_flow');

  @Input() public requisitionId: number;
  @Input() public assigned?: RequisitionAvailableFlow;
  @Input() public flows: RequisitionAvailableFlow[] = [];

  public isSelectFlowVisible = false;
  public isSelectFlowDisabled = false;
  public selectedOption?: FlowSelectOption;
  public options: FlowSelectOption[] = [];

  constructor(
    private store: Store,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private route: ActivatedRoute,
    private approvalFlowFacade: ApprovalFlowFacade,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.flows?.currentValue) {
      this.options = this.getFlowOptions(this.flows);
      this.setSelectedOption();
    }
    if (changes.assigned) {
      this.setSelectedOption();
    }

    if (changes.requisitionId?.currentValue) {
      this.isSelectFlowDisabled = !this.approvalFlowFacade.canChangeFlow;
    }

    this.isSelectFlowVisible =
      this.approvalFlowFacade.isFlowEditable &&
      (this.assigned ? this.options.length > 1 : this.options.length > 0);
  }

  public setFlow(option: FlowSelectOption) {
    this.store.dispatch(
      RequisitionsActions.setFlow({
        payload: {
          requisitionId: this.requisitionId,
          flow: { id: option.value, title: option.label },
        },
      }),
    );
  }

  private setSelectedOption() {
    this.selectedOption = this.assigned
      ? {
          value: this.assigned.id,
          label: this.assigned.custom ? this.customFlowLabel : this.assigned.title,
        }
      : undefined;
  }

  private getFlowOptions(flows: RequisitionAvailableFlow[]): FlowSelectOption[] {
    return flows.map(flow => ({
      value: flow.id!,
      label: flow.custom ? this.i18n.translate('app.requisitions.flow.custom_flow') : flow.title,
    }));
  }
}
